// Account Settings Page - User account management only
// Note: Vehicle and location info (source_address, vehicle_type, etc.) are managed via Colaboradores, not user settings
import { createElement } from '@/utils/dom'
import { authService, type User } from '@/services/auth'
import { subscriptionService } from '@/services/subscription'
import { SidebarNavigation } from '@/components/navigation'
import { router } from '@/utils/router'

interface AccountFormData {
  name: string
  company_name: string
  email: string
}

export class AccountSettingsPage {
  private user: User | null = null
  private sidebar: SidebarNavigation
  private currentSection: 'profile' | 'password' | 'subscription' = 'profile'

  constructor() {
    this.user = authService.getUser()
    this.sidebar = new SidebarNavigation({
      currentPage: 'settings',
      onNavigate: this.handleSidebarNavigation.bind(this)
    })
  }

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'account-settings-page app-layout' })

    // Add sidebar
    page.appendChild(this.sidebar.render())

    // Main content area
    const main = createElement('main', { className: 'main-content' })
    main.appendChild(this.createAccountSettingsContent())

    page.appendChild(main)
    return page
  }

  private handleSidebarNavigation(page: string): void {
    switch (page) {
      case 'dashboard':
        router.navigate('/dashboard')
        break
      case 'statistics':
        router.navigate('/statistics')
        break
      case 'generate':
        router.navigate('/dashboard?action=generate')
        break
      case 'trip-purposes':
        router.navigate('/trip-purposes')
        break
      case 'whats-new':
        router.navigate('/whats-new')
        break
      case 'admin':
        router.navigate('/admin')
        break
    }
  }

  private createAccountSettingsContent(): HTMLElement {
    const container = createElement('div', { className: 'account-settings-container' })

    container.innerHTML = `
      <div class="page-header">
        <h1 class="page-title">
          <i class="fa-solid fa-cog"></i>
          Definições da Conta
        </h1>
        <p class="page-description">Gerir informações pessoais e subscrição</p>
      </div>

      <div class="settings-layout">
        <!-- Settings Navigation -->
        <nav class="settings-nav">
          <ul class="settings-nav-list">
            <li>
              <button class="settings-nav-link ${this.currentSection === 'profile' ? 'active' : ''}" data-section="profile">
                <i class="fa-solid fa-user"></i>
                <span>Informações Pessoais</span>
              </button>
            </li>
            <li>
              <button class="settings-nav-link ${this.currentSection === 'password' ? 'active' : ''}" data-section="password">
                <i class="fa-solid fa-lock"></i>
                <span>Alterar Palavra-passe</span>
              </button>
            </li>
            <li>
              <button class="settings-nav-link ${this.currentSection === 'subscription' ? 'active' : ''}" data-section="subscription">
                <i class="fa-solid fa-credit-card"></i>
                <span>Subscrição</span>
              </button>
            </li>
          </ul>
        </nav>

        <!-- Settings Content -->
        <div class="settings-content">
          ${this.renderCurrentSection()}
        </div>
      </div>
    `

    this.attachEventListeners(container)

    return container
  }

  private renderCurrentSection(): string {
    switch (this.currentSection) {
      case 'profile':
        return this.renderProfileSection()
      case 'password':
        return this.renderPasswordSection()
      case 'subscription':
        return this.renderSubscriptionSection()
      default:
        return this.renderProfileSection()
    }
  }

  private renderProfileSection(): string {
    if (!this.user) return '<div class="error">Utilizador não encontrado</div>'

    return `
      <div class="settings-section" id="profile-section">
        <h2 class="section-title">Informações Pessoais</h2>
        <p class="section-description">Gerir informações da sua conta. Para configurar veículos e cidades de origem, utilize a gestão de colaboradores.</p>
        
        <form class="account-form" id="profile-form">
          <div class="form-grid">
            <div class="form-group">
              <label for="name">Nome Completo</label>
              <input type="text" id="name" name="name" value="${this.escapeHtml(this.user.name)}" required>
            </div>
            
            <div class="form-group">
              <label for="company_name">Nome da Empresa</label>
              <input type="text" id="company_name" name="company_name" value="${this.escapeHtml(this.user.company_name || '')}">
            </div>
            
            <div class="form-group">
              <label for="nif">NIF</label>
              <input type="text" id="nif" name="nif" value="${this.escapeHtml(this.user.nif)}" disabled>
              <small class="form-help">O NIF não pode ser alterado</small>
            </div>
            
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" value="${this.escapeHtml(this.user.email || '')}">
            </div>
          </div>
          
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" id="save-profile-btn">
              <i class="fa-solid fa-save"></i>
              Guardar Alterações
            </button>
            <button type="button" class="btn btn-secondary" id="cancel-profile-btn">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    `
  }

  private renderPasswordSection(): string {
    return `
      <div class="settings-section" id="password-section">
        <h2 class="section-title">Alterar Palavra-passe</h2>
        
        <form class="account-form" id="password-form">
          <div class="form-grid">
            <div class="form-group full-width">
              <label for="current_password">Palavra-passe Atual</label>
              <input type="password" id="current_password" name="current_password" required>
            </div>
            
            <div class="form-group">
              <label for="new_password">Nova Palavra-passe</label>
              <input type="password" id="new_password" name="new_password" minlength="8" required>
              <small class="form-help">Mínimo 8 caracteres</small>
            </div>
            
            <div class="form-group">
              <label for="confirm_password">Confirmar Nova Palavra-passe</label>
              <input type="password" id="confirm_password" name="confirm_password" minlength="8" required>
            </div>
          </div>
          
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" id="change-password-btn">
              <i class="fa-solid fa-key"></i>
              Alterar Palavra-passe
            </button>
            <button type="button" class="btn btn-secondary" id="cancel-password-btn">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    `
  }

  private renderSubscriptionSection(): string {
    if (!this.user) return '<div class="error">Utilizador não encontrado</div>'

    const subscriptionStatus = this.user.subscription_status || 'Free'
    const endDate = this.user.subscription_end_date

    return `
      <div class="settings-section" id="subscription-section">
        <h2 class="section-title">Gestão de Subscrição</h2>
        
        <div class="subscription-info">
          <div class="subscription-card">
            <div class="subscription-status ${subscriptionStatus.toLowerCase()}">
              <i class="fa-solid ${this.getStatusIcon(subscriptionStatus)}"></i>
              <div class="status-info">
                <h3 class="status-title">${this.getStatusDisplayName(subscriptionStatus)}</h3>
                <p class="status-description">${this.getStatusDescription(subscriptionStatus)}</p>
                ${endDate ? `<p class="status-date">Próxima faturação: ${this.formatDate(endDate)}</p>` : ''}
              </div>
            </div>
            
            <div class="subscription-actions">
              ${subscriptionStatus === 'active' ? `
                <button class="btn btn-danger" id="manage-subscription-btn">
                  <i class="fa-solid fa-cog"></i>
                  Gerir Subscrição
                </button>
              ` : `
                <button class="btn btn-primary" id="upgrade-subscription-btn">
                  <i class="fa-solid fa-arrow-up"></i>
                  Subscrever Plano Premium
                </button>
              `}
            </div>
          </div>
          
          ${subscriptionStatus === 'active' ? `
            <div class="plan-features">
              <h4>Funcionalidades do Plano Premium</h4>
              <ul class="features-list">
                <li><i class="fa-solid fa-check"></i> Geração ilimitada de rotas</li>
                <li><i class="fa-solid fa-check"></i> Relatórios detalhados em Excel</li>
                <li><i class="fa-solid fa-check"></i> Estatísticas avançadas</li>
                <li><i class="fa-solid fa-check"></i> Suporte técnico prioritário</li>
              </ul>
            </div>
          ` : ''}
        </div>
      </div>
    `
  }

  private attachEventListeners(container: HTMLElement): void {
    // Settings navigation
    const navLinks = container.querySelectorAll('.settings-nav-link')
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const section = (e.target as HTMLElement).closest('[data-section]')?.getAttribute('data-section') as 'profile' | 'password' | 'subscription'
        if (section && ['profile', 'password', 'subscription'].includes(section)) {
          this.switchSection(section, container)
        }
      })
    })

    // Profile form submission
    const profileForm = container.querySelector('#profile-form')
    profileForm?.addEventListener('submit', (e) => {
      e.preventDefault()
      this.handleProfileUpdate(e.target as HTMLFormElement)
    })

    // Password form submission
    const passwordForm = container.querySelector('#password-form')
    passwordForm?.addEventListener('submit', (e) => {
      e.preventDefault()
      this.handlePasswordChange(e.target as HTMLFormElement)
    })

    // Subscription management buttons
    const manageSubscriptionBtn = container.querySelector('#manage-subscription-btn')
    manageSubscriptionBtn?.addEventListener('click', () => {
      this.handleManageSubscription()
    })

    const upgradeBtn = container.querySelector('#upgrade-subscription-btn')
    upgradeBtn?.addEventListener('click', () => {
      router.navigate('/pricing')
    })

    // Cancel buttons
    const cancelProfileBtn = container.querySelector('#cancel-profile-btn')
    cancelProfileBtn?.addEventListener('click', () => {
      this.resetProfileForm(container)
    })

    const cancelPasswordBtn = container.querySelector('#cancel-password-btn')
    cancelPasswordBtn?.addEventListener('click', () => {
      this.resetPasswordForm(container)
    })
  }

  private switchSection(section: 'profile' | 'password' | 'subscription', container: HTMLElement): void {
    this.currentSection = section

    // Update navigation state
    container.querySelectorAll('.settings-nav-link').forEach(link => {
      link.classList.remove('active')
    })
    container.querySelector(`[data-section="${section}"]`)?.classList.add('active')

    // Update content
    const settingsContent = container.querySelector('.settings-content')
    if (settingsContent) {
      settingsContent.innerHTML = this.renderCurrentSection()
      this.attachSectionEventListeners(container)
    }
  }

  private attachSectionEventListeners(container: HTMLElement): void {
    // Re-attach event listeners for the current section
    if (this.currentSection === 'profile') {
      const profileForm = container.querySelector('#profile-form')
      profileForm?.addEventListener('submit', (e) => {
        e.preventDefault()
        this.handleProfileUpdate(e.target as HTMLFormElement)
      })

      const cancelBtn = container.querySelector('#cancel-profile-btn')
      cancelBtn?.addEventListener('click', () => {
        this.resetProfileForm(container)
      })
    } else if (this.currentSection === 'password') {
      const passwordForm = container.querySelector('#password-form')
      passwordForm?.addEventListener('submit', (e) => {
        e.preventDefault()
        this.handlePasswordChange(e.target as HTMLFormElement)
      })

      const cancelBtn = container.querySelector('#cancel-password-btn')
      cancelBtn?.addEventListener('click', () => {
        this.resetPasswordForm(container)
      })
    } else if (this.currentSection === 'subscription') {
      const manageSubscriptionBtn = container.querySelector('#manage-subscription-btn')
      manageSubscriptionBtn?.addEventListener('click', () => {
        this.handleManageSubscription()
      })

      const upgradeBtn = container.querySelector('#upgrade-subscription-btn')
      upgradeBtn?.addEventListener('click', () => {
        router.navigate('/pricing')
      })
    }
  }

  private async handleProfileUpdate(form: HTMLFormElement): Promise<void> {
    const formData = new FormData(form)
    const saveBtn = form.querySelector('#save-profile-btn') as HTMLButtonElement

    // Show loading state
    this.setButtonLoading(saveBtn, true)

    try {
      const updateData: Partial<AccountFormData> = {}

      // Only include changed fields (user account data only - no vehicle/location)
      const name = formData.get('name') as string
      const companyName = formData.get('company_name') as string
      const email = formData.get('email') as string

      if (name !== this.user?.name) updateData.name = name
      if (companyName !== (this.user?.company_name || '')) updateData.company_name = companyName
      if (email !== (this.user?.email || '')) updateData.email = email

      // Send update request
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        let errorMessage = 'Erro ao atualizar perfil'
        try {
          const error = await response.json()
          errorMessage = error.message || errorMessage
        } catch (e) {
          // If JSON parsing fails, use the status text
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      await response.json()

      // Update local user data
      await authService.refreshUserData()
      this.user = authService.getUser()

      // Show success message
      this.showNotification('Perfil atualizado com sucesso', 'success')

      // Refresh sidebar with updated user data
      this.sidebar.refreshUserData()

    } catch (error) {
      console.error('Profile update error:', error)
      this.showNotification(error instanceof Error ? error.message : 'Erro ao atualizar perfil', 'error')
    } finally {
      this.setButtonLoading(saveBtn, false)
    }
  }

  private async handlePasswordChange(form: HTMLFormElement): Promise<void> {
    const formData = new FormData(form)
    const changeBtn = form.querySelector('#change-password-btn') as HTMLButtonElement

    const currentPassword = formData.get('current_password') as string
    const newPassword = formData.get('new_password') as string
    const confirmPassword = formData.get('confirm_password') as string

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      this.showNotification('As palavras-passe não coincidem', 'error')
      return
    }

    // Show loading state
    this.setButtonLoading(changeBtn, true)

    try {
      const response = await fetch('/api/users/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Erro ao alterar palavra-passe')
      }

      // Clear form
      form.reset()

      // Show success message
      this.showNotification('Palavra-passe alterada com sucesso', 'success')

    } catch (error) {
      console.error('Password change error:', error)
      this.showNotification(error instanceof Error ? error.message : 'Erro ao alterar palavra-passe', 'error')
    } finally {
      this.setButtonLoading(changeBtn, false)
    }
  }

  private async handleManageSubscription(): Promise<void> {
    // Check if user has stripe customer ID
    if (!this.user?.stripe_customer_id) {
      // Refresh user data to get latest subscription info
      try {
        await authService.refreshUserData()
        this.user = authService.getUser()
      } catch (error) {
        console.error('Failed to refresh user data:', error)
      }

      // Check again after refresh
      if (!this.user?.stripe_customer_id) {
        const hasActiveSubscription = this.user?.subscription_status === 'active'
        const errorMessage = hasActiveSubscription
          ? 'Erro técnico: Portal de subscrição temporariamente indisponível. Dados de subscrição não encontrados. Por favor, contacte o suporte.'
          : 'Portal de subscrição apenas disponível para subscritores premium. Complete a sua subscrição primeiro no menu "Subscrever Plano Premium".'

        this.showNotification(errorMessage, 'error')
        return
      }
    }

    try {
      const portalSession = await subscriptionService.createBillingPortalSession()
      if (portalSession.url) {
        window.location.href = portalSession.url
      }
    } catch (error) {
      console.error('Subscription portal error:', error)
      // Check if it's a "no subscription" error
      if (error && typeof error === 'object' && 'code' in error && error.code === 'no_stripe_customer') {
        this.showNotification('É necessário ter uma subscrição ativa para aceder ao portal de subscrição', 'error')
      } else {
        this.showNotification('Erro ao abrir portal de subscrição', 'error')
      }
    }
  }

  private resetProfileForm(container: HTMLElement): void {
    const form = container.querySelector('#profile-form') as HTMLFormElement
    if (form && this.user) {
      // Reset to original values (user account data only)
      (form.querySelector('#name') as HTMLInputElement).value = this.user.name;
      (form.querySelector('#company_name') as HTMLInputElement).value = this.user.company_name || '';
      (form.querySelector('#email') as HTMLInputElement).value = this.user.email || '';
    }
  }

  private resetPasswordForm(container: HTMLElement): void {
    const form = container.querySelector('#password-form') as HTMLFormElement
    form?.reset()
  }

  private setButtonLoading(button: HTMLButtonElement, isLoading: boolean): void {
    if (isLoading) {
      button.disabled = true
      button.innerHTML = `<i class="fa-solid fa-spinner fa-spin"></i> A processar...`
    } else {
      button.disabled = false
      const originalText = button.id === 'save-profile-btn' ?
        '<i class="fa-solid fa-save"></i> Guardar Alterações' :
        '<i class="fa-solid fa-key"></i> Alterar Palavra-passe'
      button.innerHTML = originalText
    }
  }

  private showNotification(message: string, type: 'success' | 'error'): void {
    const notification = createElement('div', {
      className: `notification ${type}`
    })

    notification.innerHTML = `
      <i class="fa-solid ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}"></i>
      <span>${message}</span>
      <button class="notification-close">
        <i class="fa-solid fa-times"></i>
      </button>
    `

    notification.querySelector('.notification-close')?.addEventListener('click', () => {
      notification.remove()
    })

    document.body.appendChild(notification)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }

  private getStatusIcon(status: string): string {
    const normalizedStatus = status.toLowerCase()
    switch (normalizedStatus) {
      case 'active': return 'fa-check-circle'
      case 'canceled': return 'fa-times-circle'
      case 'past_due': return 'fa-exclamation-triangle'
      case 'trial': return 'fa-clock'
      default: return 'fa-user'
    }
  }

  private getStatusDisplayName(status: string): string {
    const normalizedStatus = status.toLowerCase()
    switch (normalizedStatus) {
      case 'active': return 'Plano Premium Ativo'
      case 'canceled': return 'Subscrição Cancelada'
      case 'past_due': return 'Pagamento em Atraso'
      case 'trial': return 'Período de Teste'
      default: return 'Plano Gratuito'
    }
  }

  private getStatusDescription(status: string): string {
    const normalizedStatus = status.toLowerCase()
    switch (normalizedStatus) {
      case 'active': return 'A sua subscrição está ativa e todas as funcionalidades estão disponíveis'
      case 'canceled': return 'A sua subscrição foi cancelada. Funcionalidades premium não estão disponíveis'
      case 'past_due': return 'Existe um pagamento em atraso. Por favor, atualize os seus dados de pagamento'
      case 'trial': return 'Está no período de teste. Pode experimentar todas as funcionalidades premium'
      default: return 'Tem acesso às funcionalidades básicas. Subscreva o plano premium para mais funcionalidades'
    }
  }

  private formatDate(timestamp: string | number): string {
    const date = new Date(typeof timestamp === 'string' ? timestamp : timestamp)
    return date.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}

// Factory function for easier instantiation
export function createAccountSettings(): HTMLElement {
  const page = new AccountSettingsPage()
  return page.render() as any
}
